'use client';

import {
  type ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import Link from 'next/link';
import { useRouter } from 'next/navigation';

/* ─── Product Card with BUY button ─── */

import { config } from '@ps/config/client';
import { AccessGuard } from '@ps/features/access-control/components/access-guard/access-guard';
import { buildMeetingCheckoutUrl } from '@ps/features/bio/custom-button/variants/calendar/functions/buildMeetingCheckoutUrl';
import { useAvailableDates } from '@ps/features/listing-page-builder/widgets/calendar-booking/hooks/use-available-dates';
import { openProductInNewTab } from '@ps/features/pdp/functions/openProductInNewTab';
import { bioUrl } from '@ps/functions/bio/bioUrls';
import { creatorInfoFromStoreProfile } from '@ps/functions/bio/creatorInfo';
import { LeadFormTriggerActionType } from '@ps/models/lib/lead-form';
import type { CreatorInfo } from '@ps/types/creator';
import { cn } from '@ps/ui/utils';
import { addDays } from 'date-fns/addDays';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Eye,
} from 'lucide-react';

import { resolveGuestPhone } from '../../checkout/functions/resolveGuestPhone';
import {
  useDispatchAction,
  useVideoClipsContext,
} from '../contexts/video-player';
import {
  type AnyVideo,
  type MeetingType,
  type VideoListing,
  fetchAvailableMeetingDates,
  fetchAvailableMeetingTypes,
  fetchClipViewCount,
  formatCount,
  getUserTimezone,
  getVideoDate,
  isPopClipVideo,
  videoInfo,
} from '../functions';

const FONT_FAMILY = 'var(--font-dm-sans), "DM Sans", sans-serif';

export type ButtonStyles = {
  bgColor?: string;
  borderColor?: string;
  textColor?: string;
};

type VideoFooterProps = {
  video: AnyVideo;
  buttonStyles?: ButtonStyles;
  className?: string;
  isRealEstateListing?: boolean;
  listingId?: string;
  /** Optional node (e.g. a link to the parent series / course) rendered under
   *  the author row. */
  ownerLink?: ReactNode;
};

export function VideoFooter({
  video,
  buttonStyles,
  className,
  isRealEstateListing,
  listingId,
  ownerLink,
}: VideoFooterProps) {
  const info = videoInfo(video);
  const profile = info.primaryProfile;
  const date = getVideoDate(video);
  const dispatchAction = useDispatchAction();
  const { creatorInfo, bioContext } = useVideoClipsContext();
  const isWelcomeVideo = video.extra?.isWelcomeVideo === true;

  const taggedItemType = isPopClipVideo(video)
    ? (video.taggedItemType ?? video.metaData?.taggedItemType)
    : undefined;
  const taggedLinks = isPopClipVideo(video)
    ? (video.taggedExternalLinks ?? video.metaData?.taggedExternalLinks)
    : undefined;
  const sellerId = isPopClipVideo(video)
    ? (video.metaData?.sellerId ?? info.sellerId)
    : undefined;

  const hasProducts =
    info.listings.length > 0 &&
    (taggedItemType === 'products' || !taggedItemType);

  // View count — dep on stable clipId string, not object ref, to
  // avoid re-fetching when the parent rerenders with a new object
  // for the same clip id.
  const clipId = isPopClipVideo(video) ? (video.clipId ?? video.id) : null;
  const [viewCount, setViewCount] = useState<number | null>(null);
  useEffect(() => {
    if (!clipId) return;
    fetchClipViewCount(clipId)
      .then(count => setViewCount(count))
      .catch(() => setViewCount(0));
  }, [clipId]);

  // Product scroll ref
  const productScrollRef = useRef<HTMLDivElement>(null);
  const scrollProducts = useCallback((direction: 'left' | 'right') => {
    const el = productScrollRef.current;
    if (!el) return;
    const cardWidth = 319; // 300px card + 19px gap
    el.scrollBy({
      left: direction === 'left' ? -cardWidth : cardWidth,
      behavior: 'smooth',
    });
  }, []);

  return (
    <div className={cn('flex w-full flex-col', className)}>
      {/* Tagged items by type */}
      {taggedItemType === 'external-links' &&
        taggedLinks &&
        taggedLinks.length > 0 && (
          <ExternalLinksPreview
            links={taggedLinks}
            buttonStyles={buttonStyles}
          />
        )}

      {taggedItemType === 'calendar' && sellerId && profile && (
        <CalendarPreview
          sellerId={sellerId}
          isRealEstateListing={isRealEstateListing}
          listingId={listingId}
          sellerProfile={creatorInfoFromStoreProfile(profile, null)}
        />
      )}

      {/* Product listings */}
      {hasProducts && (
        <ProductListings ref={productScrollRef} listings={info.listings} />
      )}

      {/* View count + scroll arrows on the same row */}
      {(hasProducts ||
        (viewCount != null &&
          viewCount >= 0 &&
          video.extra?.isWelcomeVideo !== true)) && (
        <div
          className="flex items-center justify-between"
          style={{ marginTop: 4 }}
        >
          {/* View count */}
          {viewCount != null &&
          viewCount >= 0 &&
          video.extra?.isWelcomeVideo !== true ? (
            <div
              className="flex shrink-0 items-center"
              style={{
                height: 24,
                borderRadius: 4,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                paddingLeft: 8,
                paddingRight: 8,
                paddingTop: 2,
                paddingBottom: 2,
                gap: 4,
              }}
            >
              <Eye
                className="text-content-1 shrink-0"
                style={{ width: 19, height: 13 }}
              />
              <span
                className="text-content-1"
                style={{
                  fontSize: 10,
                  textAlign: 'center',
                  fontFamily: FONT_FAMILY,
                }}
              >
                {formatCount(viewCount)}
              </span>
            </div>
          ) : (
            <div />
          )}

          {/* Scroll arrows */}
          {hasProducts && info.listings.length > 0 && (
            <div
              className="flex shrink-0 items-center justify-center"
              style={{
                width: 52,
                height: 24,
                borderRadius: 4,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                paddingTop: 2,
                paddingLeft: 4,
                paddingRight: 4,
                gap: 4,
              }}
            >
              <button
                type="button"
                className="flex items-center justify-center"
                style={{ width: 20, height: 20 }}
                onClick={e => {
                  e.stopPropagation();
                  scrollProducts('left');
                }}
              >
                <ChevronLeft
                  className="text-content-1"
                  style={{ width: 20, height: 20 }}
                />
              </button>
              <button
                type="button"
                className="flex items-center justify-center"
                style={{ width: 20, height: 20 }}
                onClick={e => {
                  e.stopPropagation();
                  scrollProducts('right');
                }}
              >
                <ChevronRight
                  className="text-content-1"
                  style={{ width: 20, height: 20 }}
                />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Author name and date */}
      <div
        className="flex flex-wrap items-center"
        style={{ marginTop: 4, gap: 10 }}
      >
        {profile ? (
          // The creator name — a link to their bio home, built from the player's
          // `bioContext` (custom-domain aware): a feed belongs to ONE creator, so
          // that context IS this clip's author, and the code stored on the clip can
          // be stale. A real-estate listing player can be embedded off-site, so it
          // keeps its absolute new-tab link.
          <Link
            href={
              isRealEstateListing
                ? `https://${config.domain}/${(profile as any).urlCode ?? profile.username}`
                : bioUrl(bioContext).home()
            }
            {...(isRealEstateListing
              ? { target: '_blank', rel: 'noopener noreferrer' }
              : {})}
            className="text-content-1 font-bold"
            style={{
              fontSize: 16,
              lineHeight: '24px',
              fontFamily: FONT_FAMILY,
            }}
            onClick={e => e.stopPropagation()}
          >
            @
            {(profile as any).storeFrontName ??
              profile.username ??
              profile.firstName ??
              'Unknown'}
          </Link>
        ) : (
          // Feeds without a per-clip author (the welcome video) surface the
          // owning creator's name instead — a link to the creator's bio (the
          // host wires `openProfile`).
          creatorInfo && (
            <button
              type="button"
              className="text-content-1 font-bold"
              style={{
                fontSize: 16,
                lineHeight: '24px',
                fontFamily: FONT_FAMILY,
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
              }}
              onClick={e => {
                e.stopPropagation();
                dispatchAction({
                  type: 'openProfile',
                  username: creatorInfo.urlCode,
                });
              }}
            >
              @{creatorInfo.name}
            </button>
          )
        )}
        {date && (
          <span
            className="text-raise/50 font-light"
            style={{
              fontSize: 12,
              lineHeight: '24px',
              fontFamily: FONT_FAMILY,
            }}
          >
            {date.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </span>
        )}
      </div>

      {/* Owner link — the parent series / course (host-provided). */}
      {ownerLink}

      {/* Video title */}
      {video.videoTitle?.trim() && (
        <p
          className="text-raise/70 line-clamp-2 font-medium"
          style={{ fontSize: 14, fontFamily: FONT_FAMILY }}
        >
          {video.videoTitle.trim()}
        </p>
      )}

      {/* Video description */}
      {video.videoDescription?.trim() && (
        <VideoDescription text={video.videoDescription.trim()} />
      )}

      {/* Video ID (clip/message ID) — meaningless for the welcome video, whose
          id is just the creator id, so it's hidden there. */}
      {info.videoId && !isWelcomeVideo && (
        <span
          className="text-raise/30"
          style={{ fontSize: 8, fontFamily: FONT_FAMILY, marginTop: 4 }}
        >
          {info.videoId}
        </span>
      )}
    </div>
  );
}

const ProductListings = forwardRef<
  HTMLDivElement,
  {
    listings: VideoListing[];
  }
>(function ProductListings({ listings }, ref) {
  // The player's clips belong to one creator, so tagged products open at that
  // creator's PDP URL (`bioContext.creatorId`/`urlCode`, custom-domain aware).
  const { bioContext } = useVideoClipsContext();
  return (
    <div
      ref={ref}
      className="no-scrollbar flex overflow-x-auto"
      style={{ gap: 19, marginBottom: 6, scrollBehavior: 'smooth' }}
    >
      {listings.map(listing => {
        const productName = listing.title ?? listing.name;
        const isFree = listing.price === 0;
        const priceText = isFree
          ? 'Free'
          : listing.price != null
            ? `$${listing.price.toFixed(2)}`
            : undefined;

        return (
          <div
            key={listing.id}
            className="flex shrink-0 overflow-hidden"
            style={{
              width: 300,
              height: 85,
              borderRadius: 12,
              border: '1px solid rgb(var(--raise) / 0.2)',
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              paddingRight: 12,
              flexDirection: 'row',
              alignItems: 'flex-start',
            }}
          >
            {/* Product image */}
            {listing.defaultImage && (
              <img
                src={listing.defaultImage}
                alt={productName ?? ''}
                className="shrink-0 object-cover"
                style={{
                  width: 90,
                  height: 85,
                  borderTopLeftRadius: 4,
                  borderBottomLeftRadius: 4,
                }}
              />
            )}

            {/* Right side: name top, price+button bottom */}
            <div
              className="flex flex-1 flex-col justify-between"
              style={{
                paddingLeft: 19,
                paddingTop: 10,
                paddingBottom: 10,
                height: '100%',
              }}
            >
              {productName && (
                <p
                  className="text-content-1 line-clamp-2 font-medium"
                  style={{
                    fontSize: 14,
                    lineHeight: '16.8px',
                    letterSpacing: 0.2,
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  {productName}
                </p>
              )}

              {priceText != null && (
                <div
                  className="flex items-center justify-between"
                  style={{ marginTop: 'auto' }}
                >
                  <span
                    className="text-content-1 font-light"
                    style={{
                      fontSize: 12,
                      lineHeight: '14.4px',
                      letterSpacing: 0.2,
                      fontFamily: FONT_FAMILY,
                    }}
                  >
                    {priceText}
                  </span>
                  <button
                    type="button"
                    className="flex items-center justify-center font-medium text-black"
                    style={{
                      width: 59,
                      height: 25,
                      borderRadius: 42,
                      backgroundColor: 'hsl(var(--brand))',
                      fontSize: 12,
                      fontFamily: FONT_FAMILY,
                      paddingTop: 4,
                      paddingBottom: 4,
                      paddingLeft: 16,
                      paddingRight: 16,
                      marginLeft: 10,
                    }}
                    onClick={e => {
                      e.stopPropagation();
                      // Always open the PDP in a new tab (RN parity —
                      // `AnyVideoProducts` navigates to the listing for every
                      // product type). The clip listing carries no slug, so this
                      // lands on the legacy `/products/<creatorId>/<id>` form
                      // until slugs flow through.
                      openProductInNewTab({
                        bioContext,
                        listingId: listing.id,
                      });
                    }}
                  >
                    {isFree ? 'GET' : 'BUY'}
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
});

/* ─── External Links Preview ─── */

function ExternalLinksPreview({
  links,
  buttonStyles,
}: {
  links: NonNullable<AnyVideo['taggedExternalLinks']>;
  buttonStyles?: ButtonStyles;
}) {
  const bgColor = buttonStyles?.bgColor || 'rgba(0, 0, 0, 0.8)';
  const borderColor = buttonStyles?.borderColor || 'rgba(255, 255, 255, 0.2)';
  const textColor = buttonStyles?.textColor || '#ffffff';

  return (
    <div
      className="no-scrollbar flex overflow-x-auto"
      style={{ gap: 10, marginBottom: 10 }}
    >
      {links.map((link, i) => (
        <div
          key={i}
          className="flex shrink-0 cursor-pointer items-center overflow-hidden transition-opacity hover:opacity-80"
          style={{
            width: 280,
            height: 60,
            borderRadius: 7.5,
            border: `1.35px solid ${borderColor}`,
            backgroundColor: bgColor,
          }}
          onClick={e => {
            e.stopPropagation();
            // Open the tagged URL in a new tab for every context (RN's web
            // path does the same); the RE branch already worked this way.
            window.open(link.url, '_blank', 'noopener,noreferrer');
          }}
        >
          {link.linkThumbnailUrl && (
            <img
              src={link.linkThumbnailUrl}
              alt=""
              className="shrink-0 object-cover"
              style={{
                width: 60,
                height: 60,
                borderTopLeftRadius: 7.5,
                borderBottomLeftRadius: 7.5,
              }}
            />
          )}
          <div
            className="flex min-w-0 flex-1 items-center gap-2.5 overflow-hidden"
            style={{ paddingLeft: 12, paddingRight: 12 }}
          >
            {/* Link icon */}
            <svg
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              className="shrink-0"
            >
              <path
                d="M6.5 8.5a3.5 3.5 0 0 0 5 0l2-2a3.536 3.536 0 0 0-5-5l-1 1"
                stroke="#7DFFB3"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M9.5 7.5a3.5 3.5 0 0 0-5 0l-2 2a3.536 3.536 0 0 0 5 5l1-1"
                stroke="#7DFFB3"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="flex min-w-0 flex-1 flex-col">
              <p
                className="w-full truncate text-left font-medium"
                style={{
                  fontSize: 13,
                  lineHeight: '16px',
                  color: textColor,
                  fontFamily: FONT_FAMILY,
                }}
              >
                {link.linkText || 'View Link'}
              </p>
              {link.linkSubtext && (
                <p
                  className="w-full truncate text-left"
                  style={{
                    fontSize: 11,
                    lineHeight: '14px',
                    color: 'hsl(var(--content-1) / 0.5)',
                    fontFamily: FONT_FAMILY,
                    marginTop: 2,
                  }}
                >
                  {link.linkSubtext}
                </p>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── Calendar Preview ─── */

const DATES_PER_PAGE = 5;
const INITIAL_VISIBLE_TYPES = 2;

/** Custom clock SVG matching the PopshopRN MeetingClockIcon */
function MeetingClockIcon({
  size = 14,
  fill = '#E6E6E6',
}: {
  size?: number;
  fill?: string;
}) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 13 13"
      fill="none"
    >
      <path
        d="M6.41667 0C5.14757 0 3.90698 0.376331 2.85176 1.0814C1.79654 1.78648 0.974105 2.78862 0.488442 3.96111C0.00278024 5.13361 -0.124291 6.42378 0.123297 7.6685C0.370886 8.91321 0.982014 10.0565 1.8794 10.9539C2.77679 11.8513 3.92013 12.4624 5.16484 12.71C6.40955 12.9576 7.69973 12.8306 8.87222 12.3449C10.0447 11.8592 11.0469 11.0368 11.7519 9.98157C12.457 8.92636 12.8333 7.68576 12.8333 6.41667C12.8315 4.71541 12.1549 3.08435 10.952 1.88138C9.74898 0.678413 8.11792 0.00179655 6.41667 0ZM6.41667 11.8462C5.34282 11.8462 4.29308 11.5277 3.40021 10.9311C2.50733 10.3345 1.81142 9.48655 1.40048 8.49444C0.989532 7.50233 0.88201 6.41064 1.09151 5.35743C1.30101 4.30421 1.81811 3.33677 2.57744 2.57744C3.33677 1.81811 4.30421 1.301 5.35743 1.09151C6.41065 0.882007 7.50233 0.989529 8.49444 1.40047C9.48655 1.81142 10.3345 2.50733 10.9311 3.4002C11.5277 4.29308 11.8462 5.34281 11.8462 6.41667C11.8445 7.85615 11.272 9.23622 10.2541 10.2541C9.23622 11.272 7.85616 11.8445 6.41667 11.8462ZM10.3654 6.41667C10.3654 6.54757 10.3134 6.67312 10.2208 6.76569C10.1283 6.85825 10.0027 6.91026 9.8718 6.91026H6.41667C6.28576 6.91026 6.16021 6.85825 6.06765 6.76569C5.97508 6.67312 5.92308 6.54757 5.92308 6.41667V2.96154C5.92308 2.83063 5.97508 2.70508 6.06765 2.61252C6.16021 2.51995 6.28576 2.46795 6.41667 2.46795C6.54758 2.46795 6.67312 2.51995 6.76569 2.61252C6.85826 2.70508 6.91026 2.83063 6.91026 2.96154V5.92308H9.8718C10.0027 5.92308 10.1283 5.97508 10.2208 6.06765C10.3134 6.16021 10.3654 6.28576 10.3654 6.41667Z"
        fill={fill}
      />
    </svg>
  );
}

function formatMeetingDurationCompact(durationMinutes: number): string {
  if (durationMinutes < 60) return `${durationMinutes} mins`;
  const hours = durationMinutes / 60;
  const isWholeHour = durationMinutes % 60 === 0;
  return `${isWholeHour ? Math.floor(hours) : hours} ${hours === 1 ? 'hour' : 'hours'}`;
}

function formatPrice(price: number): string {
  if (price === 0) return 'Free';
  return `$${price % 1 === 0 ? price.toFixed(0) : price.toFixed(2)}`;
}

function formatDate(date: Date, fmt: 'day' | 'date' | 'month'): string {
  switch (fmt) {
    case 'day':
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    case 'date':
      return String(date.getDate());
    case 'month':
      return date.toLocaleDateString('en-US', { month: 'short' });
  }
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function CalendarPreview({
  sellerId,
  isRealEstateListing,
  listingId,
  sellerProfile,
}: {
  sellerId: string;
  isRealEstateListing?: boolean;
  listingId?: string;
  sellerProfile: CreatorInfo;
}) {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isLoadingMoreDates, setIsLoadingMoreDates] = useState(false);
  const dateScrollRef = useRef<HTMLDivElement>(null);
  const [canScrollDatesLeft, setCanScrollDatesLeft] = useState(false);
  const router = useRouter();
  const [meetingTypes, setMeetingTypes] = useState<MeetingType[]>([]);
  const [meetingTypesLoading, setMeetingTypesLoading] = useState(false);
  const [selectedMeetingType, setSelectedMeetingType] =
    useState<MeetingType | null>(null);
  const [showAllTypes, setShowAllTypes] = useState(false);
  const [selectedSlotIndex, setSelectedSlotIndex] = useState<number | null>(
    null,
  );
  const timeSlotsRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Push the real `/checkout` meeting flow for the current selection. Maps the
  // clip's local `MeetingType` (no `key`) to the checkout builder's shape; the
  // builder reads only `id` + `durationMinutes` + the slot, so a derived `key`
  // just satisfies the type. `guestContact` flows from the guard's anonymous
  // contact-info step when present.
  const goToMeetingCheckout = useCallback(
    (contactData?: { name?: string; email?: string; phone?: string }) => {
      if (selectedSlotIndex == null || !selectedMeetingType) return;
      const slot = selectedMeetingType.slots[selectedSlotIndex];
      if (!slot) return;
      router.push(
        buildMeetingCheckoutUrl({
          urlCode: sellerProfile.urlCode,
          meetingType: {
            id: selectedMeetingType.id,
            key: `${selectedMeetingType.topic}|${selectedMeetingType.durationMinutes}|${selectedMeetingType.price}`,
            topic: selectedMeetingType.topic,
            durationMinutes: selectedMeetingType.durationMinutes,
            price: selectedMeetingType.price,
            slots: selectedMeetingType.slots.map(s => ({
              startTime: s.startTime,
              endTime: s.endTime,
            })),
          },
          slot: { startTime: slot.startTime, endTime: slot.endTime },
          timezone: getUserTimezone(),
          guestContact: contactData
            ? {
                name: contactData.name ?? null,
                email: contactData.email ?? null,
                // The gate submits the phone with its dial code baked in (see
                // `guest-contact-modal`), so split it into the national digits + region
                // the checkout URL carries as two params.
                ...resolveGuestPhone(contactData.phone, null),
              }
            : undefined,
        }),
      );
    },
    [router, sellerProfile.urlCode, selectedMeetingType, selectedSlotIndex],
  );

  // ── Free-only dates via useAvailableDates (RE listing videos) ──
  const freeOnly = useAvailableDates(
    isRealEstateListing ? sellerId : undefined,
  );

  // ── Default dates via raw fetch (non-RE videos) ──
  const [defaultDates, setDefaultDates] = useState<Date[]>([]);
  const [defaultLoading, setDefaultLoading] = useState(!isRealEstateListing);
  const hasMoreDefaultDatesRef = useRef(true);
  const meetingTypesCacheRef = useRef<Map<string, MeetingType[]>>(new Map());
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (isRealEstateListing) return;
    setDefaultLoading(true);
    hasMoreDefaultDatesRef.current = true;
    fetchAvailableMeetingDates(sellerId)
      .then(d => {
        setDefaultDates(d);
        if (d.length > 0) {
          void fetchAndCacheMeetingTypes(d[0]);
        }
      })
      .catch(() => setDefaultDates([]))
      .finally(() => setDefaultLoading(false));
  }, [sellerId, refreshKey, isRealEstateListing]);

  // Listen for REFRESH_CALENDAR from parent after a booking completes
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'REFRESH_CALENDAR') {
        meetingTypesCacheRef.current.clear();
        setSelectedDate(null);
        setSelectedMeetingType(null);
        setSelectedSlotIndex(null);
        setShowAllTypes(false);
        if (dateScrollRef.current) dateScrollRef.current.scrollLeft = 0;
        if (isRealEstateListing) {
          freeOnly.refresh();
        } else {
          setRefreshKey(k => k + 1);
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isRealEstateListing, freeOnly.refresh]);

  // Unified dates / loading
  const dates = isRealEstateListing ? freeOnly.dates : defaultDates;
  const loading = isRealEstateListing ? freeOnly.isLoading : defaultLoading;

  // The dates endpoint is a window from `startDate`, so the non-RE path has to
  // page forward too — otherwise the visitor only ever sees the first window
  // while the creator preview (`CalendarAvailability`) walks the whole calendar.
  const loadMoreDefaultDates = useCallback(async () => {
    if (!hasMoreDefaultDatesRef.current) return;
    const lastDate = defaultDates[defaultDates.length - 1];
    if (lastDate == null) return;
    const more = await fetchAvailableMeetingDates(
      sellerId,
      addDays(lastDate, 1),
    ).catch(() => [] as Date[]);
    if (more.length === 0) {
      hasMoreDefaultDatesRef.current = false;
      return;
    }
    setDefaultDates(prev => [...prev, ...more]);
  }, [defaultDates, sellerId]);

  const fetchAndCacheMeetingTypes = useCallback(
    async (date: Date): Promise<MeetingType[]> => {
      const key = date.toISOString().split('T')[0]!;
      const cached = meetingTypesCacheRef.current.get(key);
      if (cached) return cached;

      let types: MeetingType[];
      if (isRealEstateListing) {
        const items = await freeOnly.fetchMeetingTypes(date);
        types = items.map(item => ({
          id: item.id,
          topic: item.topic,
          durationMinutes: item.durationMinutes,
          price: item.price,
          slots: item.slots,
        }));
      } else {
        types = await fetchAvailableMeetingTypes(sellerId, date);
      }

      meetingTypesCacheRef.current.set(key, types);
      return types;
    },
    [sellerId, isRealEstateListing, freeOnly.fetchMeetingTypes],
  );

  // Fetch meeting types when date selected
  useEffect(() => {
    if (!selectedDate) {
      setMeetingTypes([]);
      return;
    }
    setMeetingTypesLoading(true);
    fetchAndCacheMeetingTypes(selectedDate)
      .then(types => setMeetingTypes(types))
      .catch(() => setMeetingTypes([]))
      .finally(() => setMeetingTypesLoading(false));
  }, [selectedDate, fetchAndCacheMeetingTypes]);

  // Update scroll indicators for time slots
  const updateScrollIndicators = useCallback(() => {
    const el = timeSlotsRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    updateScrollIndicators();
  }, [selectedMeetingType, updateScrollIndicators]);

  // Update left arrow visibility on scroll
  const updateDateScrollState = useCallback(() => {
    const el = dateScrollRef.current;
    if (!el) return;
    setCanScrollDatesLeft(el.scrollLeft > 0);
  }, []);

  const [minimized, setMinimized] = useState(false);

  const showDates = !loading && dates.length > 0;
  const showMeetingTypes =
    !loading &&
    !meetingTypesLoading &&
    selectedDate &&
    !selectedMeetingType &&
    meetingTypes.length > 0;
  const showSelectedType = !loading && selectedDate && selectedMeetingType;
  const timeSlots = selectedMeetingType?.slots ?? [];
  const showTimeSlots = showSelectedType && timeSlots.length > 0;
  const visibleMeetingTypes = showAllTypes
    ? meetingTypes
    : meetingTypes.slice(0, INITIAL_VISIBLE_TYPES);
  const hasMoreTypes = meetingTypes.length > INITIAL_VISIBLE_TYPES;

  return (
    <div
      onClick={e => e.stopPropagation()}
      style={{
        borderRadius: 12,
        border: '1px solid rgb(var(--raise) / 0.2)',
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        marginTop: 15,
        width: '100%',
      }}
    >
      {/* Title row with minimize toggle */}
      <div
        className="flex items-center justify-between"
        style={{
          marginBottom: 8,
          cursor: 'pointer',
        }}
        onClick={e => {
          e.stopPropagation();
          setMinimized(v => !v);
        }}
      >
        <p
          style={{
            fontSize: 12,
            fontWeight: 500,
            lineHeight: '14.4px',
            letterSpacing: 0.2,
            fontFamily: FONT_FAMILY,
            color: 'hsl(var(--content-1))',
            margin: 0,
          }}
        >
          Book 1:1 Time with me
        </p>
        {showDates &&
          selectedDate &&
          (minimized ? (
            <ChevronDown
              size={16}
              color="hsl(var(--content-1))"
              strokeWidth={2}
            />
          ) : (
            <ChevronUp
              size={16}
              color="hsl(var(--content-1))"
              strokeWidth={2}
            />
          ))}
      </div>

      {/* Loader and dates are always visible; rest hidden when minimized */}
      {loading ? (
        <div
          className="flex items-center justify-center"
          style={{ padding: 20 }}
        >
          <div
            className="animate-spin"
            style={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              border: '2px solid rgb(var(--raise) / 0.2)',
              borderTopColor: 'hsl(var(--brand))',
            }}
          />
        </div>
      ) : showDates ? (
        <>
          {/* Date pills — horizontally scrollable with overlay arrows */}
          <div style={{ position: 'relative' }}>
            {/* Scrollable date row */}
            <div
              ref={dateScrollRef}
              className="no-scrollbar flex overflow-x-auto"
              // Trailing padding = the overlay arrow's width, so the LAST
              // date can scroll fully clear of the always-visible right
              // arrow instead of ending underneath it (QA: "give a bit
              // extra space at the end so the last one is visible").
              style={{ gap: 8, scrollBehavior: 'smooth', paddingRight: 24 }}
              onScroll={updateDateScrollState}
            >
              {dates.map((date, i) => {
                const isSelected =
                  selectedDate != null && isSameDay(date, selectedDate);
                return (
                  <button
                    key={i}
                    type="button"
                    className="flex shrink-0 flex-col items-center justify-center"
                    style={{
                      width: 54,
                      height: 74,
                      borderRadius: 8,
                      backgroundColor: isSelected
                        ? 'hsl(var(--brand))'
                        : 'hsl(var(--line))',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                    }}
                    onClick={e => {
                      e.stopPropagation();
                      setSelectedDate(date);
                      setSelectedMeetingType(null);
                      setShowAllTypes(false);
                      setSelectedSlotIndex(null);
                      setMinimized(false);
                    }}
                  >
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 300,
                        lineHeight: '16.8px',
                        letterSpacing: 0.2,
                        textAlign: 'center',
                        color: isSelected
                          ? 'hsl(var(--content-on-brand))'
                          : 'hsl(var(--content-3))',
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      {formatDate(date, 'day')}
                    </span>
                    <span
                      style={{
                        fontSize: 16,
                        fontWeight: 500,
                        lineHeight: '16px',
                        letterSpacing: 0.2,
                        textAlign: 'center',
                        marginTop: 2,
                        color: isSelected
                          ? 'hsl(var(--content-on-brand))'
                          : 'hsl(var(--content-1))',
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      {formatDate(date, 'date')}
                    </span>
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 300,
                        lineHeight: '16.8px',
                        letterSpacing: 0.2,
                        textAlign: 'center',
                        marginTop: 2,
                        color: isSelected
                          ? 'hsl(var(--content-on-brand))'
                          : 'hsl(var(--content-3))',
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      {formatDate(date, 'month')}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Left overlay arrow — a compact solid CHIP centered on the edge,
                not a full-height gradient bar. The old edge gradient washed
                over the first date cell and hid it (QA: "gradient too wide,
                date partially hidden"); a small round chip keeps the arrow
                clearly visible while covering only a sliver of the edge cell so
                the date underneath stays readable. */}
            {canScrollDatesLeft && (
              <button
                type="button"
                aria-label="Previous dates"
                className="flex items-center justify-center"
                style={{
                  position: 'absolute',
                  left: 2,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(0,0,0,0.9)',
                  border: 'none',
                  cursor: 'pointer',
                  zIndex: 2,
                }}
                onClick={e => {
                  e.stopPropagation();
                  const el = dateScrollRef.current;
                  if (el) el.scrollLeft -= 62 * DATES_PER_PAGE;
                }}
              >
                <ChevronLeft
                  size={16}
                  color="hsl(var(--content-1))"
                  strokeWidth={2}
                />
              </button>
            )}

            {/* Right overlay arrow — always visible when dates overflow */}
            {(dates.length > DATES_PER_PAGE || isLoadingMoreDates) && (
              <button
                type="button"
                aria-label="Next dates"
                className="flex items-center justify-center"
                disabled={isLoadingMoreDates}
                style={{
                  // Same compact solid chip as the left arrow (QA).
                  position: 'absolute',
                  right: 2,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(0,0,0,0.9)',
                  border: 'none',
                  cursor: isLoadingMoreDates ? 'default' : 'pointer',
                  zIndex: 2,
                }}
                onClick={e => {
                  e.stopPropagation();
                  const el = dateScrollRef.current;
                  if (el) el.scrollLeft += 62 * DATES_PER_PAGE;
                  // Preload one page ahead — trigger when within ~2 pages of the end
                  if (el && !isLoadingMoreDates) {
                    const remainingScroll =
                      el.scrollWidth - (el.scrollLeft + el.clientWidth);
                    if (remainingScroll < 62 * DATES_PER_PAGE * 2) {
                      setIsLoadingMoreDates(true);
                      const more = isRealEstateListing
                        ? freeOnly.loadMoreDates()
                        : loadMoreDefaultDates();
                      more.finally(() => setIsLoadingMoreDates(false));
                    }
                  }
                }}
              >
                {isLoadingMoreDates ? (
                  <div
                    className="animate-spin"
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      border: '2px solid rgb(var(--raise) / 0.2)',
                      borderTopColor: 'hsl(var(--brand))',
                    }}
                  />
                ) : (
                  <ChevronRight
                    size={16}
                    color="hsl(var(--content-1))"
                    strokeWidth={2}
                  />
                )}
              </button>
            )}
          </div>

          {/* "Available meeting types" title — shown whenever a date is selected */}
          {!minimized && selectedDate && (
            <p
              style={{
                fontSize: 12,
                fontWeight: 500,
                lineHeight: '14.4px',
                letterSpacing: 0.2,
                marginBottom: 8,
                marginTop: 12,
                color: 'hsl(var(--content-1))',
                fontFamily: FONT_FAMILY,
              }}
            >
              Available meeting types
            </p>
          )}

          {/* State 2: Meeting type list — hidden when minimized */}
          {!minimized && showMeetingTypes && (
            <div>
              {visibleMeetingTypes.map(type => (
                <button
                  key={type.id}
                  type="button"
                  className="flex w-full items-center"
                  style={{
                    backgroundColor: 'hsl(var(--line))',
                    borderRadius: 8,
                    padding: '10px 12px',
                    marginBottom: 6,
                    border: 'none',
                    cursor: 'pointer',
                  }}
                  onClick={e => {
                    e.stopPropagation();
                    setSelectedMeetingType(type);
                    setShowAllTypes(false);
                  }}
                >
                  {/* Topic — left, truncates */}
                  <span
                    style={{
                      flex: 1,
                      fontSize: 14,
                      fontWeight: 300,
                      lineHeight: '16.8px',
                      letterSpacing: 0.2,
                      color: 'hsl(var(--content-1))',
                      fontFamily: FONT_FAMILY,
                      textAlign: 'left',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {type.topic}
                  </span>
                  {/* Duration pill */}
                  <span
                    className="flex shrink-0 items-center"
                    style={{
                      borderRadius: 13,
                      backgroundColor: 'hsl(var(--brand) / 0.14)',
                      padding: '4px 8px',
                      marginLeft: 8,
                      gap: 4,
                    }}
                  >
                    <MeetingClockIcon size={14} fill="#E6E6E6" />
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 500,
                        lineHeight: '14.4px',
                        letterSpacing: 0.2,
                        color: 'hsl(var(--content-2))',
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      {formatMeetingDurationCompact(type.durationMinutes)}
                    </span>
                  </span>
                  {/* Price */}
                  <span
                    style={{
                      marginLeft: 10,
                      fontSize: 14,
                      fontWeight: 500,
                      lineHeight: '16.8px',
                      letterSpacing: 0.2,
                      color: 'hsl(var(--content-2))',
                      fontFamily: FONT_FAMILY,
                      flexShrink: 0,
                    }}
                  >
                    {formatPrice(type.price)}
                  </span>
                </button>
              ))}
              {/* See more / See less */}
              {hasMoreTypes && (
                <button
                  type="button"
                  className="flex w-full items-center justify-center"
                  style={{
                    marginTop: 4,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    gap: 4,
                  }}
                  onClick={e => {
                    e.stopPropagation();
                    setShowAllTypes(prev => !prev);
                  }}
                >
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 500,
                      lineHeight: '14.4px',
                      letterSpacing: 0.2,
                      color: 'hsl(var(--brand-text))',
                      fontFamily: FONT_FAMILY,
                      textAlign: 'center',
                    }}
                  >
                    {showAllTypes ? 'See less' : 'See more'}
                  </span>
                  {showAllTypes ? (
                    <ChevronUp
                      size={14}
                      color="hsl(var(--brand-text))"
                      strokeWidth={2}
                    />
                  ) : (
                    <ChevronDown
                      size={14}
                      color="hsl(var(--brand-text))"
                      strokeWidth={2}
                    />
                  )}
                </button>
              )}
            </div>
          )}

          {/* State 3: Selected meeting type + Available Times — hidden when minimized */}
          {!minimized && showSelectedType && (
            <div style={{ marginTop: 10 }}>
              {/* Selected type pill + Change button */}
              <div className="flex items-center" style={{ gap: 8 }}>
                {/* Pill — topic left, duration+price right */}
                <div
                  className="flex min-w-0 flex-1 items-center"
                  style={{
                    borderRadius: 12,
                    border: '1px solid hsl(var(--brand))',
                    backgroundColor: 'hsl(var(--surface-1))',
                    padding: '8px 12px',
                    overflow: 'hidden',
                  }}
                >
                  {/* Topic */}
                  <span
                    style={{
                      flex: 1,
                      fontSize: 14,
                      fontWeight: 300,
                      lineHeight: '16.8px',
                      letterSpacing: 0.2,
                      color: 'hsl(var(--content-1))',
                      fontFamily: FONT_FAMILY,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {selectedMeetingType!.topic}
                  </span>
                  {/* Duration pill */}
                  <span
                    className="flex shrink-0 items-center"
                    style={{
                      borderRadius: 13,
                      backgroundColor: 'hsl(var(--brand) / 0.14)',
                      padding: '4px 8px',
                      marginLeft: 8,
                      gap: 4,
                    }}
                  >
                    <MeetingClockIcon size={14} fill="#E6E6E6" />
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 500,
                        lineHeight: '14.4px',
                        letterSpacing: 0.2,
                        color: 'hsl(var(--content-2))',
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      {formatMeetingDurationCompact(
                        selectedMeetingType!.durationMinutes,
                      )}
                    </span>
                  </span>
                  {/* Price */}
                  <span
                    style={{
                      marginLeft: 10,
                      fontSize: 14,
                      fontWeight: 500,
                      lineHeight: '16.8px',
                      letterSpacing: 0.2,
                      color: 'hsl(var(--content-2))',
                      fontFamily: FONT_FAMILY,
                      flexShrink: 0,
                    }}
                  >
                    {formatPrice(selectedMeetingType!.price)}
                  </span>
                </div>
                {/* Change button — outside the pill */}
                <button
                  type="button"
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 500,
                    lineHeight: '14.4px',
                    letterSpacing: 0.2,
                    color: 'hsl(var(--brand-text))',
                    fontFamily: FONT_FAMILY,
                    flexShrink: 0,
                    padding: 0,
                  }}
                  onClick={e => {
                    e.stopPropagation();
                    setSelectedMeetingType(null);
                    setShowAllTypes(false);
                    setSelectedSlotIndex(null);
                  }}
                >
                  Change
                </button>
              </div>

              {/* Available Times */}
              {showTimeSlots && (
                <div style={{ marginTop: 10 }}>
                  <div
                    className="flex items-center justify-between"
                    style={{ marginBottom: 8 }}
                  >
                    <span
                      style={{
                        fontSize: 12,
                        lineHeight: '16px',
                        color: 'white',
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      Available Times
                    </span>
                    {/* Scroll arrows */}
                    <div className="flex items-center" style={{ gap: 8 }}>
                      <button
                        type="button"
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: canScrollLeft ? 'pointer' : 'default',
                          padding: 0,
                        }}
                        onClick={e => {
                          e.stopPropagation();
                          timeSlotsRef.current?.scrollBy({
                            left: -200,
                            behavior: 'smooth',
                          });
                        }}
                      >
                        <ChevronLeft
                          size={16}
                          color={
                            canScrollLeft ? 'white' : 'rgba(255, 255, 255, 0.3)'
                          }
                          strokeWidth={2}
                        />
                      </button>
                      <button
                        type="button"
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: canScrollRight ? 'pointer' : 'default',
                          padding: 0,
                        }}
                        onClick={e => {
                          e.stopPropagation();
                          timeSlotsRef.current?.scrollBy({
                            left: 200,
                            behavior: 'smooth',
                          });
                        }}
                      >
                        <ChevronRight
                          size={16}
                          color={
                            canScrollRight
                              ? 'white'
                              : 'rgba(255, 255, 255, 0.3)'
                          }
                          strokeWidth={2}
                        />
                      </button>
                    </div>
                  </div>

                  {/* Time slot pills */}
                  <div
                    ref={timeSlotsRef}
                    className="no-scrollbar flex overflow-x-auto"
                    style={{ gap: 8 }}
                    onScroll={updateScrollIndicators}
                  >
                    {timeSlots.map((slot, index) => {
                      const startTime = new Date(
                        slot.startTime,
                      ).toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true,
                      });
                      const durationLabel = formatMeetingDurationCompact(
                        selectedMeetingType!.durationMinutes,
                      );
                      const isSlotSelected = selectedSlotIndex === index;

                      return (
                        <button
                          key={index}
                          type="button"
                          className="flex shrink-0 items-center"
                          style={{
                            backgroundColor: isSlotSelected
                              ? '#7DFFB3'
                              : 'rgba(255, 255, 255, 0.05)',
                            borderRadius: 8,
                            border: `1px solid ${isSlotSelected ? 'hsl(var(--brand))' : 'rgba(255, 255, 255, 0.10)'}`,
                            padding: '6px 10px',
                            cursor: 'pointer',
                            gap: 4,
                          }}
                          onClick={e => {
                            e.stopPropagation();
                            setSelectedSlotIndex(index);
                          }}
                        >
                          {/* Clock icon */}
                          <MeetingClockIcon
                            size={12}
                            fill={isSlotSelected ? 'black' : '#E6E6E6'}
                          />
                          {/* Time */}
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 500,
                              lineHeight: '14.4px',
                              letterSpacing: 0.2,
                              color: isSlotSelected
                                ? 'black'
                                : 'hsl(var(--content-2))',
                              fontFamily: FONT_FAMILY,
                            }}
                          >
                            {startTime}
                          </span>
                          {/* Duration badge */}
                          <span
                            style={{
                              backgroundColor: isSlotSelected
                                ? 'rgba(0, 0, 0, 0.64)'
                                : 'hsl(var(--brand) / 0.14)',
                              borderRadius: 4,
                              padding: '1.5px 6px 2px',
                              fontSize: 7,
                              fontWeight: 500,
                              lineHeight: '10.5px',
                              textAlign: 'center',
                              color: 'hsl(var(--brand-text))',
                              fontFamily: FONT_FAMILY,
                            }}
                          >
                            {durationLabel}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Book Now button — appears after a time slot is selected.
                  Gated behind the SAME access guard the bio's
                  `WiredBookMeetingButton` uses, then pushes the real `/checkout`
                  meeting flow (free $0 → Stripe setup, and paid, alike; NOT the
                  old `bookMeetingSlot` payment-bypass). Real-estate keeps its
                  `skip-follow` + listing lead trigger; everything else gets the
                  follow + anonymous-contact gate. Full-page nav (booking is a
                  checkout), leaving the clip like the bio does. */}
              {showTimeSlots && selectedSlotIndex != null && (
                <AccessGuard
                  storeId={sellerId}
                  guard={{
                    kind: isRealEstateListing ? 'skip-follow' : 'follow',
                    ...(isRealEstateListing
                      ? {}
                      : { anonymousContactInfo: true }),
                    triggers: [
                      ...(isRealEstateListing && listingId
                        ? [
                            {
                              type: LeadFormTriggerActionType.RealEstateListing,
                              id: listingId,
                            },
                          ]
                        : []),
                      {
                        type: LeadFormTriggerActionType.Calendar,
                        id: selectedMeetingType!.id,
                      },
                    ],
                  }}
                  sellerProfile={sellerProfile}
                  onAccessGranted={contactData =>
                    goToMeetingCheckout(contactData ?? undefined)
                  }
                >
                  {(canProceed, requestAccess, isReady) => (
                    <button
                      type="button"
                      disabled={!isReady}
                      style={{
                        width: '100%',
                        marginTop: 12,
                        padding: '10px 0',
                        borderRadius: 8,
                        backgroundColor: 'hsl(var(--brand))',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: 14,
                        fontWeight: 600,
                        lineHeight: '16.8px',
                        letterSpacing: 0.2,
                        color: 'hsl(var(--content-on-brand))',
                        fontFamily: FONT_FAMILY,
                      }}
                      onClick={e => {
                        e.stopPropagation();
                        if (!canProceed) {
                          requestAccess();
                          return;
                        }
                        goToMeetingCheckout();
                      }}
                    >
                      Book Now
                    </button>
                  )}
                </AccessGuard>
              )}

              {/* No time slots */}
              {showSelectedType && timeSlots.length === 0 && (
                <p
                  className="text-raise/80"
                  style={{
                    fontSize: 10,
                    marginTop: 10,
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  No available times for this meeting type
                </p>
              )}
            </div>
          )}

          {/* Loading meeting types */}
          {!minimized && selectedDate && meetingTypesLoading && (
            <div
              className="flex items-center justify-center"
              style={{ padding: 16, marginTop: 12 }}
            >
              <div
                className="animate-spin"
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  border: '2px solid rgb(var(--raise) / 0.2)',
                  borderTopColor: 'hsl(var(--brand))',
                }}
              />
            </div>
          )}

          {/* No meeting types for selected date */}
          {!minimized &&
            selectedDate &&
            !selectedMeetingType &&
            !meetingTypesLoading &&
            meetingTypes.length === 0 && (
              <p
                className="text-raise/80"
                style={{
                  fontSize: 10,
                  marginTop: 16,
                  fontFamily: FONT_FAMILY,
                }}
              >
                No available slots for this date
              </p>
            )}
        </>
      ) : (
        <p
          className="text-raise/80"
          style={{ fontSize: 10, fontFamily: FONT_FAMILY }}
        >
          No available dates
        </p>
      )}
    </div>
  );
}

/* ─── Video Description ─── */

function VideoDescription({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={{ maxHeight: 16 * 5, overflow: 'auto' }}>
      <p
        className={cn('text-raise/70', !expanded && 'line-clamp-2')}
        style={{ fontSize: 12, lineHeight: '16px', fontFamily: FONT_FAMILY }}
      >
        {text}
      </p>
      {text.length > 100 && (
        <button
          type="button"
          className="text-brand font-medium"
          style={{ fontSize: 12, marginTop: 2, fontFamily: FONT_FAMILY }}
          onClick={e => {
            e.stopPropagation();
            setExpanded(v => !v);
          }}
        >
          {expanded ? 'Show less' : 'See more'}
        </button>
      )}
    </div>
  );
}
